import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";

import arrowIcon from "../../assets/previous_icon.png";
import editIcon from "../../assets/edit_icon.png";
import closeIcon from "../../assets/close_icon.png";
import saveIcon from "../../assets/save_icon.png";
import checkIcon from "../../assets/check_icon.png";
import printIcon from "../../assets/printer_icon.png";

import CustomButton from "../../components/CustomButton";
import AreYouSureModal from "../../components/AreYouSureModal";
import { useNavigate } from "react-router-dom";
import CustomInputWithUnit from "../../components/CustomInputWithUnit";
import { useReactToPrint } from "react-to-print";
import PrintableOrderView from "./PrintableOrderView";
import CustomCheckbox from "../../components/CustomCheckbox";

const FabricOrderList = ({ data, setData, orderStatus = "PENDING" }) => {
  // HOOKS
  const { t } = useTranslation("general");
  const navigate = useNavigate();

  // STATES
  const [selectedColors, setSelectedColors] = useState({});
  const [collapsed, setCollapsed] = useState({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [editableData, setEditableData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [areYouSureModal, setAreYouSureModal] = useState(false);
  const printRef = useRef();

  // Transform backend response
  const transformedData = data?.fabricOrderItem?.reduce((acc, item) => {
    const fabricCode = item.fabricRuler?.code;
    if (!fabricCode) return acc;

    if (!acc[fabricCode]) {
      acc[fabricCode] = {
        code: fabricCode,
        fabricColor: [],
      };
    }

    acc[fabricCode].fabricColor.push({
      code: item.fabricColor?.colorCode,
      amount: item.requestedQuantity,
      price: item.price,
      measuringUnit: item?.measuringUnit,
      currency: item?.currency,
    });

    return acc;
  }, {});

  const fabricList = Object.values(
    isEditMode && editableData ? editableData : transformedData || {}
  );

  const handleCheckboxChange = (fabricCode, colorCode) => {
    setSelectedColors((prev) => ({
      ...prev,
      [fabricCode]: {
        ...prev[fabricCode],
        [colorCode]: !prev[fabricCode]?.[colorCode],
      },
    }));
  };

  const toggleCollapse = (fabricCode) => {
    setCollapsed((prev) => ({
      ...prev,
      [fabricCode]: !prev[fabricCode],
    }));
  };

  const handleEdit = () => {
    const prechecked = {};
    const deepCopy = {};

    Object.entries(transformedData).forEach(([fabricCode, fabric]) => {
      prechecked[fabricCode] = {};
      deepCopy[fabricCode] = {
        code: fabric.code,
        fabricColor: fabric.fabricColor.map((color) => {
          prechecked[fabricCode][color.code] = true;
          return { ...color };
        }),
      };
    });

    setSelectedColors(prechecked);
    setEditableData(deepCopy);
    setIsEditMode(true);
  };

  const handleCancel = () => {
    setIsEditMode(false);
    setEditableData(null);
    setSelectedColors({});
  };

  const handleSave = async () => {
    if (!editableData) return;

    setLoading(true);

    const updatedOrder = JSON.parse(JSON.stringify(data));
    const deletedItemIds = [];

    updatedOrder.fabricOrderItem = updatedOrder.fabricOrderItem.reduce(
      (acc, item) => {
        const fabricCode = item.fabricRuler?.code;
        const colorCode = item.fabricColor?.colorCode;

        const isChecked = selectedColors?.[fabricCode]?.[colorCode];

        if (isChecked) {
          const updated = editableData?.[fabricCode]?.fabricColor?.find(
            (c) => c.code === colorCode
          );
          acc.push({
            ...item,
            requestedQuantity: parseFloat(updated.amount),
            price: parseFloat(updated.price),
          });
        } else {
          deletedItemIds.push(item.id);
        }

        return acc;
      },
      []
    );

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_URL}/updateFabricOrder/${updatedOrder?.id}`,
        {
          ...updatedOrder,
          deletedItemIds,
        }
      );

      setData(response.data);
      setIsEditMode(false);
      setEditableData(null);
      setSelectedColors({});
    } catch (error) {
      console.error("Save failed:", error);
      alert(t("failedChanges"));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (fabricCode, colorCode, field, value) => {
    setEditableData((prev) => {
      const updated = { ...prev };
      updated[fabricCode].fabricColor = updated[fabricCode].fabricColor.map(
        (color) =>
          color.code === colorCode ? { ...color, [field]: value } : color
      );
      return updated;
    });
  };

  const handleConfrimOrder = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_URL}/confirmFabricOrder/${data?.id}`,
        {
          organizationId: data?.organizationId,
        },
        {
          params: {
            id: data?.id,
          },
        }
      );

      if (response.status === 200) {
        setAreYouSureModal(false);
        navigate(-1);
      }
    } catch (error) {
      console.error("Error confirming order:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAttemptToConfirmReceived = () => setAreYouSureModal(true);

  const preparePrintData = () => {
    const printData = fabricList.map((fabric) => ({
      code: fabric.code,
      colors: fabric.fabricColor.map((color) => ({
        colorCode: color.code,
        colorName: color.code,
        quantity: color.amount,
        price: color.price,
        measuringUnit: color?.measuringUnit,
        currency: color?.currency,
      })),
    }));

    return printData;
  };

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    pageStyle: `
      @page { 
          size: A4; 
          margin: 5mm;
          width: 21cm;
          height: 29.7cm;
      }
      @media print {
        body { 
          -webkit-print-color-adjust: exact; 
          padding: 10mm;
        }
        .printable-order-view { display: flex !important;
        }
      }
    `,
  });

  return (
    <div className="c-fabric-table">
      <div className="o-row o-row--horizontal-align_space-between">
        <h1 class="t-headline-1">
          {t("orderNumber")}: {data?.orderNumber}
        </h1>
        {orderStatus === "PENDING" && (
          <>
            {!isEditMode ? (
              <CustomButton
                variant="blank"
                title={t("edit")}
                onClick={handleEdit}
                icon={editIcon}
                iconSize={18}
              />
            ) : (
              <div className="o-row o-row--gap-size_300">
                <CustomButton
                  title={t("cancel")}
                  onClick={handleCancel}
                  variant="blank"
                  icon={closeIcon}
                  iconSize={14}
                />
                <CustomButton
                  title={loading ? t("saving") : t("save")}
                  onClick={handleSave}
                  icon={saveIcon}
                  iconSize={16}
                  disabled={loading}
                />
              </div>
            )}
          </>
        )}
      </div>

      {fabricList.map((fabric) => (
        <div key={fabric.code} className="c-fabric-block">
          <div
            className="c-fabric-header"
            onClick={() => toggleCollapse(fabric.code)}
          >
            <span
              className="c-collapse-icon"
              style={{
                cursor: "pointer",
                transform: collapsed[fabric.code]
                  ? "rotate(0deg)"
                  : "rotate(-90deg)",
                transition: "transform 0.3s ease",
              }}
            >
              <img src={arrowIcon} alt="Collapse" style={{ width: 16 }} />
            </span>
            <span className="c-fabric-code">
              {t("code")}: {fabric.code}
            </span>
          </div>

          {!collapsed[fabric.code] && (
            <table className="c-fabric-inner-table">
              <thead>
                <tr>
                  {isEditMode && <th></th>}
                  <th>{t("colorCode")}</th>
                  <th>{t("quantity")}</th>
                  <th>{t("price")}</th>
                </tr>
              </thead>
              <tbody>
                {fabric?.fabricColor?.map((color) => (
                  <tr key={color.code}>
                    {isEditMode && (
                      <td>
                        <CustomCheckbox
                          checked={!!selectedColors[fabric.code]?.[color.code]}
                          onChange={() =>
                            handleCheckboxChange(fabric.code, color.code)
                          }
                        />
                      </td>
                    )}
                    <td>{color.code}</td>
                    <td>
                      {isEditMode ? (
                        <CustomInputWithUnit
                          type="text"
                          value={color.amount}
                          onChange={(e) =>
                            handleInputChange(
                              fabric.code,
                              color.code,
                              "amount",
                              e.target.value
                            )
                          }
                          flex
                          unit={t(color?.measuringUnit?.toLowerCase())}
                        />
                      ) : (
                        color.amount +
                        ` ${t(color?.measuringUnit?.toLowerCase())}`
                      )}
                    </td>
                    <td>
                      {isEditMode ? (
                        <CustomInputWithUnit
                          type="money"
                          value={color.price}
                          onChange={(e) =>
                            handleInputChange(
                              fabric.code,
                              color.code,
                              "price",
                              e.target.value
                            )
                          }
                          unit={t(color?.currency?.toLowerCase())}
                          flex
                        />
                      ) : (
                        `${color.price} ${t(color?.currency?.toLowerCase())}`
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ))}

      {orderStatus == "PENDING" && (
        <div className="u-margin--top_500 u-margin--bottom_500 o-row o-row--horizontal-align_end o-row--gap-size_400">
          {!isEditMode && (
            <>
              <CustomButton
                variant="gray"
                title={t("print")}
                onClick={handlePrint}
                icon={printIcon}
                iconSize={18}
              />

              <CustomButton
                title={t("orderReceived")}
                variant="green"
                onClick={handleAttemptToConfirmReceived}
                icon={checkIcon}
                iconSize={18}
              />
            </>
          )}

          {areYouSureModal ? (
            <AreYouSureModal
              onClose={() => setAreYouSureModal(false)}
              onConfirm={handleConfrimOrder}
              customMessage="areYouSureToConfirmReceivedOrder"
              closeTitle="cancel"
              confirmTitle="confirm"
              confirmIconType="whiteCheck"
              loading={loading}
              confirm
            />
          ) : null}
        </div>
      )}

      <PrintableOrderView
        ref={printRef}
        data={preparePrintData()}
        currency={{ value: data?.fabricOrderItem?.[0]?.currency || "USD" }}
        measuringUnit={{
          title: data?.fabricOrderItem?.[0]?.measuringUnit || "METER",
        }}
        orderDetails={{
          orderNumber: data?.orderNumber,
          date: new Date(data?.createdAt).toLocaleDateString(),
          companyName: data?.organization?.name,
          createdBy: data?.createdByUser?.name,
        }}
      />
    </div>
  );
};

export default FabricOrderList;
